import { useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { titles } from '@/mocks/titles';
import { filterTitles, type FilterState } from '@/utils/titles';
import Breadcrumb from '@/components/base/Breadcrumb';
import TitleRow from '@/components/feature/TitleRow';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 5;

const sortOptions: { value: FilterState['sort']; label: string }[] = [
  { value: 'default', label: '默认排序' },
  { value: 'rating', label: '评分最高' },
  { value: 'newest', label: '最近更新' },
  { value: 'year', label: '年份最新' },
];

export default function ListPage() {
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get('q') || '';
  const genreParam = searchParams.get('genre') || '';
  const typeParam = searchParams.get('type') || '';

  const [type, setType] = useState(typeParam);
  const [sort, setSort] = useState<FilterState['sort']>('default');
  const [visible, setVisible] = useState(PAGE_SIZE);

  const baseList = useMemo(() => {
    let list = titles;
    if (type && (type === 'movie' || type === 'tv')) {
      list = list.filter((item) => item.type === type);
    }
    if (genreParam) {
      list = list.filter((item) => item.genres.includes(genreParam));
    }
    if (keyword) {
      const kw = keyword.toLowerCase();
      list = list.filter(
        (item) => item.title.toLowerCase().includes(kw) || item.genres.some((g) => g.includes(keyword)),
      );
    }
    return list;
  }, [type, genreParam, keyword]);

  const sorted = useMemo(() => filterTitles(baseList, { sort }), [baseList, sort]);
  const shown = sorted.slice(0, visible);
  const hasMore = visible < sorted.length;

  const contextLabel = keyword
    ? `关键词「${keyword}」`
    : genreParam
      ? `类型「${genreParam}」`
      : type === 'movie'
        ? '电影'
        : type === 'tv'
          ? '电视剧'
          : '全部影视';

  usePageMeta({
    title: '综合影视列表 - 秋霞电影网',
    description: `秋霞电影网综合影视列表，当前筛选${contextLabel}，共 ${sorted.length} 部影视内容，支持类型、年份、评分多维排序。`,
    keywords: '影视列表,秋霞电影网,电影电视剧筛选,最新影视资源',
  });

  const typeChips = [    { value: '', label: '全部' },
    { value: 'movie', label: '电影' },
    { value: 'tv', label: '电视剧' },
  ];

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视列表' }]} />

      <div className="mt-4 mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">综合影视列表</h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600">
          当前筛选：<span className="text-accent-700 font-semibold">{contextLabel}</span> · 共 {sorted.length} 部
        </p>
      </div>

      <div className="rounded-lg bg-background-100 border border-background-200 px-4 md:px-5 py-3 mb-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground-700">类型</span>
          {typeChips.map((chipItem) => (
            <button
              key={chipItem.value}
              type="button"
              onClick={() => {
                setType(chipItem.value);
                setVisible(PAGE_SIZE);
              }}
              className={`px-3 py-1.5 rounded-full text-xs md:text-sm border transition-colors cursor-pointer whitespace-nowrap ${
                type === chipItem.value
                  ? 'bg-primary-500 text-background-50 border-primary-500'
                  : 'bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300'
              }`}
            >
              {chipItem.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 md:ml-auto">
          <span className="text-sm font-medium text-foreground-700">排序</span>
          {sortOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setSort(option.value);
                setVisible(PAGE_SIZE);
              }}
              className={`px-3 py-1.5 rounded-full text-xs md:text-sm border transition-colors cursor-pointer whitespace-nowrap ${
                sort === option.value
                  ? 'bg-accent-500 text-primary-950 border-accent-500'
                  : 'bg-background-50 text-foreground-700 border-background-300 hover:border-accent-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {shown.length > 0 ? (
        <>
          <div className="flex flex-col gap-3 md:gap-4">
            {shown.map((item) => (
              <TitleRow key={item.id} item={item} />
            ))}
          </div>

          {hasMore && (
            <div className="mt-8 flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                className="h-11 px-8 rounded-md border border-primary-300 text-primary-700 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                加载更多（剩余 {sorted.length - visible} 部）
              </button>
              <span className="text-xs text-foreground-500">
                已显示 {shown.length} / {sorted.length}
              </span>
            </div>
          )}
        </>
      ) : (
        <div className="py-16 flex flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl mb-4">
            <i className="ri-file-search-line"></i>
          </span>
          <p className="text-foreground-700 mb-4">没有找到符合条件的影视内容</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/"
              className="h-10 px-5 flex items-center rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
            <Link
              to="/list"
              className="h-10 px-5 flex items-center rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              清空筛选
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}